/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { Box, Stack } from '@atgfr8/atg-ui-tailwind';
import { CautionInput } from '@components/TextField/CautionInput';
import { CautionSelect } from '@components/Select/CautionSelect';
import {
  orderStore,
  TrackingProviders,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { ChevronDownIcon, ChevronUpIcon } from '@atgfr8/icons';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { createEffect, createSignal, Show } from 'solid-js';
import { reeferEquipmentTypes } from '@common/order/constants';

import { ReeferWarning } from './ReeferWarning';

type PropsT = {
  load: ILoadEditViewModel;
  tabIndex: number;
};

export const ReeferSettings = (props: PropsT) => {
  const [visible, setVisible] = createSignal(false);
  const [alwaysVisible, setAlwaysVisible] = createSignal(false);
  const [isInErrorState, setIsInErrorState] = createSignal(false);
  const [isReeferDirty, setIsReeferDirty] = createSignal(false);

  createEffect(() => {
    if (
      (!!props.load.equipment &&
        reeferEquipmentTypes.includes(props.load.equipment)) ||
      !!props.load.minTemperature ||
      !!props.load.maxTemperature ||
      !!props.load.temperatureUnit ||
      !!props.load.runMode ||
      !!props.load.tempInstructions
    ) {
      if (
        !!props.load.equipment &&
        reeferEquipmentTypes.includes(props.load.equipment)
      ) {
        setAlwaysVisible(true);
      } else {
        setAlwaysVisible(false);
      }

      setVisible(true);
    } else {
      setAlwaysVisible(false);
      setVisible(false);
    }
  });

  createEffect(() => {
    // this is the validation logic for the reefer settings, its complex but its at least understandable
    //if temperature instructions exist, we need to check to see if any of the other temperature fields are filled out
    // if they are not filled out, we are not in an error state, but if one of them are filled out, we are in an error
    // state until all of them are filled out
    if (props.load.tempInstructions) {
      if (
        props.load.minTemperature ||
        props.load.maxTemperature ||
        props.load.temperatureUnit ||
        props.load.runMode
      ) {
        //we have temp instructions and at least one other field filled out, we need ALL fields filled out to not be in error state
        if (
          props.load.minTemperature === null ||
          props.load.minTemperature === undefined ||
          props.load.maxTemperature === null ||
          props.load.maxTemperature === undefined ||
          !props.load.temperatureUnit ||
          !props.load.runMode
        ) {
          setIsInErrorState(true);
        } else {
          setIsInErrorState(false);
        }
      } else {
        //only temp instructions is filled out, no error
        setIsInErrorState(false);
      }
    } else {
      //else we need to validate and see if the other fields are filled out, they must ALL be filled out in this case
      if (
        props.load.minTemperature === null ||
        props.load.minTemperature === undefined ||
        props.load.maxTemperature === null ||
        props.load.maxTemperature === undefined ||
        !props.load.temperatureUnit ||
        !props.load.runMode
      ) {
        setIsInErrorState(true);
      } else {
        setIsInErrorState(false);
      }
    }
  });

  createEffect(() => {
    if (orderStore.resetDirty) {
      setIsReeferDirty(false);
    }
  });

  const setTempValue = (
    val: string,
    target: 'maxTemperature' | 'minTemperature',
  ) => {
    let numVal: number | null | undefined = parseFloat(val);

    if (isNaN(numVal)) {
      numVal = undefined;
    }

    updateLoadPropertyAtIndex({
      [target]: numVal,
    });
  };

  return (
    <Box
      borderRadius="5px"
      backgroundColor={isInErrorState() ? '#e5c5cc' : '#fff'}
      style={{
        'box-shadow':
          '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
        border: `1px solid ${isInErrorState() ? '#B00020' : 'transparent'}`,
      }}
    >
      <Stack
        padding="8px 16px"
        sx={{
          cursor: !alwaysVisible() ? 'pointer' : undefined,
          'font-size': '14px',
          'line-height': '157%',
          'flex-direction': 'row',
        }}
        fontWeight="500"
        color="#123B50"
        justifyContent="space-between"
        onClick={() => !alwaysVisible() && setVisible((prev) => !prev)}
      >
        <Box>
          Reefer Settings<span class="text-red-500">*</span>
        </Box>
        <Show when={!alwaysVisible()}>
          <Box>
            <Show when={visible()} fallback={<ChevronDownIcon />}>
              <ChevronUpIcon />
            </Show>
          </Box>
        </Show>
      </Stack>
      <Show when={visible()}>
        <Stack padding="8px" gap="8px">
          <Show when={isInErrorState()}>
            <Box
              color="#B00020"
              class="leading-[20px]"
              fontSize="12px"
              padding="0 8px"
            >
              Min Temp, Max Temp, Unit of Measure, and Run Mode are required, OR
              Temperature Instructions is required.
            </Box>
          </Show>
          <Show
            when={
              Boolean(
                props.load.trackingServiceId === TrackingProviders.TruckerTools,
              ) && isReeferDirty()
            }
          >
            <ReeferWarning />
          </Show>
          <Stack direction="row" gap="8px">
            <CautionInput
              caution={Boolean(
                props.load.trackingServiceId === TrackingProviders.TruckerTools,
              )}
              resetDirty={orderStore.resetDirty}
              label="Min Temp."
              value={props.load.minTemperature ?? ''}
              onChange={(e: { target: { value: string } }) => {
                setTempValue(e.target.value, 'minTemperature');
              }}
              error={
                orderStore.orderFormError &&
                orderStore.orderFormError[
                  `loads[${props.tabIndex}].minTemperature`
                ]
              }
              onInput={() => {
                setIsReeferDirty(true);
              }}
            />
            <CautionInput
              caution={Boolean(
                props.load.trackingServiceId === TrackingProviders.TruckerTools,
              )}
              label="Max Temp."
              resetDirty={orderStore.resetDirty}
              value={props.load.maxTemperature ?? ''}
              onChange={(e: { target: { value: string } }) => {
                setTempValue(e.target.value, 'maxTemperature');
              }}
              error={
                orderStore.orderFormError &&
                orderStore.orderFormError[
                  `loads[${props.tabIndex}].maxTemperature`
                ]
              }
              onInput={() => {
                setIsReeferDirty(true);
              }}
            />
          </Stack>
          <Box>
            <CautionSelect
              caution={Boolean(
                props.load.trackingServiceId === TrackingProviders.TruckerTools,
              )}
              label="Unit of Measure"
              placeholder="Select Unit of Measure"
              value={props.load.temperatureUnit ?? ''}
              error={
                orderStore.orderFormError &&
                orderStore.orderFormError[
                  `loads[${props.tabIndex}].temperatureUnit`
                ]
              }
              onChange={(e: { target: { value: string } }) => {
                if (e.target.value === '') {
                  updateLoadPropertyAtIndex({
                    temperatureUnit: undefined,
                  });
                  return;
                }
                setIsReeferDirty(true);
                updateLoadPropertyAtIndex({
                  temperatureUnit: e.target.value as
                    | ILoadEditViewModel['temperatureUnit']
                    | undefined,
                });
              }}
              options={[
                { id: 'Fahrenheit', name: 'Fahrenheit' },
                { id: 'Celsius', name: 'Celsius' },
              ]}
              resetDirty={orderStore.resetDirty}
            />
          </Box>
          <Box>
            <CautionSelect
              caution={Boolean(
                props.load.trackingServiceId === TrackingProviders.TruckerTools,
              )}
              label="Run Mode"
              placeholder="Select Run Mode"
              value={props.load.runMode ?? ''}
              onChange={(e: { target: { value: string } }) => {
                if (e.target.value === '') {
                  updateLoadPropertyAtIndex({
                    runMode: undefined,
                  });
                  return;
                }
                setIsReeferDirty(true);
                updateLoadPropertyAtIndex({
                  runMode:
                    (e.target.value as ILoadEditViewModel['runMode']) ?? null,
                });
              }}
              options={[
                { id: 'Continuous', name: 'Continuous' },
                { id: 'Cycle', name: 'Cycle' },
              ]}
              resetDirty={orderStore.resetDirty}
              error={
                (orderStore.orderFormError &&
                  orderStore.orderFormError[
                    `loads[${props.tabIndex}].runMode`
                  ]) ||
                orderStore.orderFormSoftErrors[
                  `loads[${props.tabIndex}].runMode`
                ]
              }
            />
          </Box>
          <Box height="20px" class="flex flex-col" justifyContent="center">
            <Box
              border="1px solid rgba(0, 0, 0, 0.12)"
              class="flex"
              justifyContent="center"
              alignItems="center"
              height={0}
            >
              <Box
                color="rgba(0, 0, 0, 0.60)"
                fontSize="12px"
                fontWeight="500"
                p="0 5px"
                backgroundColor={isInErrorState() ? '#e5c5cc' : '#fff'}
              >
                OR
              </Box>
            </Box>
          </Box>
          <Box>
            <CautionInput
              caution={Boolean(
                props.load.trackingServiceId === TrackingProviders.TruckerTools,
              )}
              resetDirty={orderStore.resetDirty}
              label="Temperature Instructions"
              value={props.load.tempInstructions ?? ''}
              onChange={(e: { target: { value: string } }) =>
                updateLoadPropertyAtIndex({
                  tempInstructions: e.target.value,
                })
              }
              onInput={() => {
                setIsReeferDirty(true);
              }}
              error={
                orderStore.orderFormError &&
                orderStore.orderFormError[
                  `loads[${props.tabIndex}].tempInstructions`
                ]
              }
              disabled={orderStore.isReadOnly}
            />
          </Box>
        </Stack>
      </Show>
    </Box>
  );
};
